import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import {nftButtonSx} from '@/styles/nftButtonSx';
import parse from 'html-react-parser';
import {colors, borderRadius, gradients, overlay, typography, fontSize, lineHeight, fontWeight, zIndex} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

export interface SaasProductCtaProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    labels?: string[];
    primaryButtonText: string;
    secondaryButtonText?: string;
    // Pass either a click handler (e.g. openForm) or a link (e.g. meetingLink)
    onPrimaryClick?: () => void;
    onSecondaryClick?: () => void;
    primaryButtonLink?: string;
    secondaryButtonLink?: string;
}

const CtaButton = ({
    label,
    className,
    variant,
    link,
    onClick
}: {
    label: string;
    className: string;
    variant: 'contained' | 'outlined';
    link?: string;
    onClick?: () => void;
}) => {
    // "nft-button" (globals.scss `.nft-button`, now `nftButtonSx`) is one of two className
    // values this shared button renders (the other is the outlined "transparent-button").
    const isNftButton = className.split(/\s+/).includes('nft-button');
    const resolvedClassName = isNftButton
        ? className
              .split(/\s+/)
              .filter((token) => token !== 'nft-button')
              .join(' ')
        : className;
    const button = (
        <MUIButton
            size="medium"
            variant={variant}
            disableRipple={true}
            className={resolvedClassName}
            label={label}
            aria-label={label}
            endIcon={<ArrowForwardIcon />}
            onClick={link ? undefined : onClick}
            sx={isNftButton ? {...ctaButtonSx, ...nftButtonSx} : ctaButtonSx}
        />
    );
    return link ? (
        <Link href={link} target="_blank" rel="noopener noreferrer" className="remove-underline">
            {button}
        </Link>
    ) : (
        button
    );
};

const CheckIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{flexShrink: 0}}
        aria-hidden="true"
    >
        <path d="M4 11.5L8.5 16L18 7" stroke={colors.status.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ctaButtonSx = {whiteSpace: 'nowrap'} as const;

const contentLayerSx = {position: 'relative', zIndex: zIndex.raised} as const;

const sectionSx = {
    background: gradients.darkSection,
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    position: 'relative',
    py: 4,
    px: 5
} as const;

const headingSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[24], md: fontSize[32]},
    lineHeight: {xs: lineHeight[32], md: lineHeight[40]},
    color: colors.text.inverse,
    mb: 1.5
} as const;

const subheadingSx = {
    ...typography.body,
    lineHeight: lineHeight[28],
    color: overlay.whiteStrong
} as const;

const bulletTextSx = {
    ...typography.bodySm,
    color: overlay.whiteStrong
} as const;

const SaasProductCta = ({
    eyebrow,
    heading,
    subheading,
    labels,
    primaryButtonText,
    secondaryButtonText,
    onPrimaryClick,
    onSecondaryClick,
    primaryButtonLink,
    secondaryButtonLink
}: SaasProductCtaProps) => {
    return (
        <MUIContainer>
            <Box sx={sectionSx}>
                <Box sx={contentLayerSx}>
                    <Box display="flex" flexDirection={{xs: 'column', md: 'row'}} alignItems={{md: 'center'}} gap={{xs: 4, md: 6}}>
                        <Box flex={{md: 7}} minWidth={0}>
                            {eyebrow && <Eyebrow text={eyebrow} onDark />}
                            <Typography component="p" className="font-headers" sx={headingSx}>
                                {heading}
                            </Typography>
                            {subheading && (
                                <Typography className="font-regular-text solution-link" sx={subheadingSx}>
                                    {parse(subheading)}
                                </Typography>
                            )}
                            {labels && labels.length > 0 && (
                                <Box display="flex" flexWrap="wrap" rowGap={2} columnGap={3} mt={3}>
                                    {labels.map((label) => (
                                        <Box display="flex" alignItems="center" gap={1} key={label}>
                                            <CheckIcon />
                                            <Typography className="font-regular-text" sx={bulletTextSx}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Buttons — 30% */}
                        <Box flex={{md: 3}} minWidth={0} width="100%">
                            <Box display="flex" flexDirection="column" gap={2} alignItems={{xs: 'stretch', md: 'flex-end'}}>
                                <CtaButton
                                    label={primaryButtonText}
                                    variant="contained"
                                    className="animated-button moving-icon-button nft-button"
                                    link={primaryButtonLink}
                                    onClick={onPrimaryClick}
                                />
                                {secondaryButtonText && (
                                    <CtaButton
                                        label={secondaryButtonText}
                                        variant="outlined"
                                        className="moving-icon-button animated-button transparent-button"
                                        link={secondaryButtonLink}
                                        onClick={onSecondaryClick}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(SaasProductCta);
